import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { type Board } from '@air/api/types';

import { KanbanBoardComponent } from '~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanItem/shared/KanbanBoardComponent';
import { type KanbanBoardProps } from '~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanItem/shared/kanbanItemTypes';
import { PrivateGalleryBoardAncestors } from '~/components/PrivateGallery/PrivateGalleryBoardAncestors';
import { usePrivateBoardMoreMenuOptions } from '~/components/v2/components/board/gallery/usePrivateBoardMoreMenuOptions';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useOpenBoardInNewTab } from '~/hooks/useOpenBoardInNewTab';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import { canChangeBoardCustomFields, canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export const PrivateKanbanBoard = memo(({ board, kanbanDndItemData, style }: KanbanBoardProps<Board>) => {
  const { boardPermissions } = useBoardPermissions({
    boardId: board.id,
  });
  const { isSearchActive } = useIsSearchActive();
  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const getItemMenuOptions = usePrivateBoardMoreMenuOptions(board);
  const { goToBoardPage } = useGoToPrivateBoardPage();
  const { openBoardInNewTab } = useOpenBoardInNewTab();
  const onBoardClick = useCallback(
    () => goToBoardPage({ board, trackLocation: 'gallery-item' }),
    [board, goToBoardPage],
  );
  const onBoardCmdClick = useCallback(
    () => openBoardInNewTab({ board, trackLocation: 'gallery-item' }),
    [board, openBoardInNewTab],
  );

  const canViewCustomFields = canSeeBoardCustomFields(boardPermissions);
  const canChangeCustomFields = canChangeBoardCustomFields(boardPermissions);

  return (
    <KanbanBoardComponent
      board={board}
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      kanbanDndItemData={kanbanDndItemData}
      onClick={onBoardClick}
      onCmdClickTitle={onBoardCmdClick}
      isSelectable={false}
      ancestors={
        isSearchActive ? (
          <PrivateGalleryBoardAncestors libraryTitle={board.library?.title} ancestors={board.ancestors} />
        ) : undefined
      }
      style={style}
      dndDisabled={isMobileAgent || !canChangeCustomFields}
      canViewCustomFields={canViewCustomFields}
    />
  );
}, isEqual);

PrivateKanbanBoard.displayName = 'PrivateKanbanBoard';
